import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { MapPin, Sparkles, Loader2, Flag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import RatingStars from "../components/RatingStars";

export default function ProviderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reportReason, setReportReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/providers/${id}`);
      setProvider(data);
    } catch {
      toast.error("Prestataire introuvable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const reserve = async () => {
    if (!user) { nav("/login"); return; }
    setBooking(true);
    try {
      const { data } = await api.post("/checkout/session", {
        item_type: "provider",
        item_id: provider.id,
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de paiement");
      setBooking(false);
    }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", {
        target_type: "provider", target_id: provider.id,
        rating: reviewRating, comment: reviewComment, criteria: {},
      });
      toast.success("Avis publié");
      setReviewComment("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const submitReport = async () => {
    if (!user) { nav("/login"); return; }
    if (reportReason.trim().length < 3) {
      toast.error("Veuillez préciser le motif");
      return;
    }
    try {
      await api.post("/reports", {
        target_type: "provider", target_id: provider.id, reason: reportReason,
      });
      toast.success("Signalement envoyé à la modération");
      setReportReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12"><Loader2 className="animate-spin h-6 w-6" /></div>;
  if (!provider) return null;

  const img = provider.images?.[0] || "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=1600";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="aspect-video rounded-md overflow-hidden mb-8 bg-cream-100">
        <img src={img} alt={provider.name} className="w-full h-full object-cover" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Badge variant="outline" className="uppercase tracking-wider">{provider.category}</Badge>
            {provider.rating_avg > 0 && <RatingStars rating={provider.rating_avg} count={provider.rating_count} />}
          </div>
          <p className="text-label text-muted-foreground mb-3">Prestataire événementiel</p>
          <h1 data-testid="provider-title" className="font-heading font-light tracking-editorial text-4xl lg:text-5xl text-ink leading-tight mb-6">
            {provider.name}
          </h1>

          <div className="flex flex-wrap gap-4 mb-6 text-sm">
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-accent" /> Zone : {provider.zone}</span>
          </div>

          <p className="text-muted-foreground leading-relaxed mb-8">
            {provider.description?.trim() || "Aucune description fournie pour ce prestataire."}
          </p>

          {provider.services?.length > 0 && (
            <div className="mb-10">
              <h2 className="font-heading font-medium text-xl text-ink mb-4 tracking-tight">Services proposés</h2>
              <div className="flex flex-wrap gap-2">
                {provider.services.map((s) => (
                  <span key={s} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md bg-cream-100 text-ink/80">
                    <Sparkles className="h-3 w-3" /> {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-medium text-xl text-ink tracking-tight">Avis ({provider.reviews?.length || 0})</h2>
              {user && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button data-testid="provider-review-cta" variant="outline" size="sm">Laisser un avis</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Noter ce prestataire</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium mb-2">Note</p>
                        <div className="flex gap-1">
                          {[1,2,3,4,5].map(i => (
                            <button key={i} onClick={() => setReviewRating(i)} type="button">
                              <span className={`text-2xl ${i <= reviewRating ? "text-accent" : "text-ink/30"}`}>★</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <Textarea
                        placeholder="Partagez votre expérience..."
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        data-testid="provider-review-textarea"
                      />
                      <Button onClick={submitReview} data-testid="provider-review-submit" className="w-full bg-ink hover:bg-ink/85 rounded-none">Publier</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            {provider.reviews?.length ? provider.reviews.map((r) => (
              <div key={r.id} className="bg-white border border-ink/10 rounded-md p-4 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{r.user_name}</p>
                    {r.verified && <Badge className="bg-trust/10 text-trust border border-trust/20 text-xs">Vérifié</Badge>}
                  </div>
                  <RatingStars rating={r.rating} showNumber={false} size={14} />
                </div>
                <p className="text-sm text-muted-foreground">{r.comment}</p>
              </div>
            )) : <p className="text-sm text-muted-foreground">Pas encore d'avis.</p>}
          </div>
        </div>

        {/* Sidebar: Booking + Report */}
        <aside className="lg:sticky lg:top-24 self-start space-y-4">
          <div className="bg-white border border-ink/10 rounded-md p-6">
            <p className="text-label text-muted-foreground mb-3">Fourchette tarifaire</p>
            <p className="font-heading font-black text-3xl text-ink mb-1">
              {provider.price_min}€ <span className="text-lg text-ink/40">→</span> {provider.price_max}€
            </p>
            <p className="text-xs text-muted-foreground mb-6">Tarif indicatif, commission plateforme 20%</p>

            <Button
              data-testid="provider-reserve"
              onClick={reserve}
              disabled={booking}
              className="w-full bg-ink hover:bg-ink/85 text-cream rounded-none"
            >
              {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Réserver ce prestataire"}
            </Button>
            <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-ink/10">
              Paiement sécurisé via Stripe. Un acompte au tarif minimum est prélevé à la réservation.
            </p>
          </div>

          {user && (
            <Dialog>
              <DialogTrigger asChild>
                <button
                  data-testid="provider-report-btn"
                  className="w-full text-xs text-muted-foreground hover:text-destructive flex items-center justify-center gap-1.5 py-2"
                >
                  <Flag className="h-3 w-3" /> Signaler ce prestataire
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Signaler ce prestataire</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Décrivez ce qui pose problème (contenu trompeur, prix abusif, comportement, etc.). Notre équipe examinera votre signalement.
                  </p>
                  <Textarea
                    placeholder="Motif du signalement..."
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    data-testid="report-textarea"
                  />
                  <Button onClick={submitReport} data-testid="report-submit" className="w-full bg-ink hover:bg-ink/85 rounded-none">Envoyer le signalement</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </aside>
      </div>
    </div>
  );
}
